import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRightCircle, BellRing, Printer, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate, ugx } from "@/lib/format";

export default function InvoiceView({ kind = "invoices" }) {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const isEstimate = kind === "estimates";
  const isPO = kind === "purchase-orders";
  const isQuote = isEstimate || isPO;
  const { canWrite } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [company, setCompany] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/${kind}/${invoiceId}`).then(({ data }) => setInvoice(data))
    .catch((err) => setInvoice(err.response?.status === 404 ? false : "error"));

  useEffect(() => {
    load();
    api.get("/company").then(({ data }) => setCompany(data));
    /* eslint-disable-next-line */
  }, [invoiceId, kind]);

  const convert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(isPO ? `/purchase-orders/${invoiceId}/receive` : `/estimates/${invoiceId}/convert`);
      toast.success(isPO ? `Received — bill ${data.number} created` : `Converted to invoice ${data.number}`);
      if (isPO) load(); else navigate(`/invoices/${data.id}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const sendMail = async (endpoint) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/${endpoint}`);
      toast.success(`${endpoint === "send-email" ? "Invoice" : "Reminder"} emailed to ${data.to}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (invoice === "error") return <p className="text-sm text-rose-600">Could not load this document. Please try again.</p>;
  if (invoice === false) return <p className="text-sm text-rose-600">Document not found.</p>;
  if (!invoice || !company) return <p className="text-sm text-slate-500">Loading…</p>;

  const paid = invoice.amount_paid || 0;
  const party = invoice.customer || invoice.vendor;

  return (
    <div data-testid="invoice-view-page">
      <div className="mb-5 flex items-center justify-between print:hidden">
        <Link to={`/${kind}`} data-testid="back-to-invoices-link" className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} /> Back to {isEstimate ? "estimates" : isPO ? "purchase orders" : "invoices"}
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && isEstimate && invoice.status !== "converted" && invoice.status !== "declined" && (
            <button data-testid="view-convert-estimate-button" disabled={busy} onClick={convert}
              className="flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              <ArrowRightCircle size={15} /> Convert to invoice
            </button>
          )}
          {canWrite && isPO && invoice.status !== "received" && invoice.status !== "cancelled" && (
            <button data-testid="view-receive-po-button" disabled={busy} onClick={convert}
              className="flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              <ArrowRightCircle size={15} /> Receive → create bill
            </button>
          )}
          {isPO && invoice.bill_number && (
            <span data-testid="view-received-bill-note" className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">
              Received as {invoice.bill_number}
            </span>
          )}
          {isEstimate && invoice.invoice_id && (
            <Link to={`/invoices/${invoice.invoice_id}`} data-testid="view-converted-invoice-link"
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              View invoice {invoice.invoice_number}
            </Link>
          )}
          {canWrite && !isQuote && (
            <>
              <button data-testid="view-send-invoice-email-button" disabled={busy} onClick={() => sendMail("send-email")}
                className="flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                <Send size={15} /> Email invoice
              </button>
              {invoice.amount_due > 0.01 && (
                <button data-testid="view-send-reminder-button" disabled={busy} onClick={() => sendMail("send-reminder")}
                  className="flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  <BellRing size={15} /> Send reminder
                </button>
              )}
            </>
          )}
          <button data-testid="print-invoice-button" onClick={() => window.print()}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Printer size={16} /> Print / Save as PDF
          </button>
        </div>
      </div>

      {invoice.last_emailed_at && (
        <p className="mb-4 text-xs text-slate-500 print:hidden" data-testid="last-emailed-note">
          Last emailed to the customer on {shortDate(invoice.last_emailed_at)} ({invoice.last_email_kind}).
        </p>
      )}

      <div id="invoice-print-area" className="mx-auto max-w-3xl border border-slate-200 bg-white p-8 sm:p-10 print:border-0 print:p-0">
        <div className="flex flex-col justify-between gap-6 border-b-2 border-slate-900 pb-6 sm:flex-row">
          <div className="flex items-start gap-4">
            {company.logo_url && (
              <img src={company.logo_url} alt={company.name} data-testid="invoice-company-logo"
                className="h-16 w-auto max-w-[180px] object-contain" />
            )}
            <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900" data-testid="invoice-company-name">{company.name}</h1>
            <p className="mt-1 max-w-xs text-sm text-slate-600">{company.address}</p>
            <p className="mt-2 text-xs text-slate-500">
              {company.tin && <>TIN: {company.tin}</>}{company.vat_number && <> · VAT No: {company.vat_number}</>}
            </p>
            <p className="text-xs text-slate-500">{company.phone} {company.email && `· ${company.email}`}</p>
            </div>
          </div>
          <div className="sm:text-right">
            <p className="font-display text-3xl font-extrabold uppercase tracking-tight text-emerald-700">{isEstimate ? "Quotation" : isPO ? "Purchase Order" : "Tax Invoice"}</p>
            <p className="mt-2 text-sm font-semibold text-slate-900" data-testid="invoice-view-number">{invoice.number}</p>
            <p className="text-sm text-slate-600">Date: {shortDate(invoice.date)}</p>
            <p className="text-sm text-slate-600">{isEstimate ? "Valid until" : isPO ? "Expected delivery" : "Due"}: {shortDate(isEstimate ? invoice.expiry_date : isPO ? invoice.expected_date : invoice.due_date)}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-slate-500">Status: {invoice.status}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{isPO ? "Supplier" : "Billed to"}</p>
            <p className="mt-1 font-display text-base font-bold text-slate-900" data-testid="invoice-customer-name">{party?.name || "—"}</p>
            {party?.address && <p className="text-sm text-slate-600">{party.address}</p>}
            {party?.tin && <p className="text-xs text-slate-500">TIN: {party.tin}</p>}
            {party?.email && <p className="text-xs text-slate-500">{party.email}</p>}
            {party?.phone && <p className="text-xs text-slate-500">{party.phone}</p>}
          </div>
          <div className="sm:text-right">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{isEstimate ? "Quoted amount" : isPO ? "Order total" : "Amount due"}</p>
            <p className="mt-1 font-display text-3xl font-extrabold text-slate-900" data-testid="invoice-view-amount-due">{ugx(invoice.amount_due)}</p>
            <p className="text-xs text-slate-500">Currency: {company.currency}</p>
          </div>
        </div>

        <table className="mt-8 w-full text-sm" data-testid="invoice-view-lines">
          <thead>
            <tr className="border-y border-slate-300 text-left text-xs uppercase tracking-wider text-slate-600">
              <th className="py-2">Description</th>
              <th className="py-2 text-right">Qty</th>
              <th className="py-2 text-right">Unit price</th>
              <th className="py-2 text-right">VAT</th>
              <th className="py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2.5 text-slate-800">{l.description || "—"}</td>
                <td className="py-2.5 text-right tabular-nums">{l.quantity}</td>
                <td className="py-2.5 text-right tabular-nums">{money(l.unit_price)}</td>
                <td className="py-2.5 text-right tabular-nums text-slate-600">{l.vat_rate}%</td>
                <td className="py-2.5 text-right tabular-nums font-medium">{money(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs text-sm">
            {[["Subtotal", invoice.subtotal], ["VAT 18%", invoice.vat_amount],
              [`Withholding tax ${invoice.wht_rate || 0}%`, -invoice.wht_amount],
              ...(isQuote ? [] : [["Amount paid", -paid]])].map(([label, v]) => (
              <div key={label} className="flex justify-between border-b border-slate-100 py-1.5 text-slate-600">
                <span>{label}</span><span className="tabular-nums">{money(v)}</span>
              </div>
            ))}
            <div className="mt-2 flex justify-between bg-slate-900 px-3 py-2.5 font-display font-bold text-white">
              <span>{isEstimate ? "Total quoted" : isPO ? "Order total" : "Balance due"}</span><span className="tabular-nums">{ugx(invoice.amount_due)}</span>
            </div>
          </div>
        </div>

        {invoice.payments?.length > 0 && (
          <div className="mt-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Payments received</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-600">
              {invoice.payments.map((p) => (
                <li key={p.id} className="flex justify-between border-b border-slate-100 py-1">
                  <span>{shortDate(p.date)} · {p.method}{p.reference ? ` · ${p.reference}` : ""}</span>
                  <span className="tabular-nums">{money(p.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {invoice.notes && (
          <p className="mt-8 border-t border-slate-200 pt-4 text-sm text-slate-600">{invoice.notes}</p>
        )}
        <p className="mt-8 text-center text-xs text-slate-500">
          Payment in {company.currency} · Prepared with NeroBooks · {isEstimate ? "This quotation is not a tax invoice." : isPO ? "Please quote the PO number on your delivery note and invoice." : "Thank you for your business."}
        </p>
      </div>
    </div>
  );
}
